<template>
  <section v-if="error">
    <br />
    <p class="error">{{ error }}</p>
  </section>

  <section class="grid-container">
    <div>
      <br />
      <WizardProgress :currentStep="stepIndex + 1" :totalSteps="steps.length" />
    </div>

    <div class="page-container grid-container wizard">
      <div class="grid-x grid-margin-x">
        <component
          v-if="loaded"
          :key="stepIndex"
          :is="currentStepComponent"
          ref="stepComponentRef"
          :formData="formData"
          @next="nextStep"
          @prev="prevStep"
          @update-form="handleFormUpdate"
        />

        <div v-if="stepIndex < steps.length - 1" id="wizard-navigation" class="cell small-12">
          <button
            class="btn btn-secondary"
            @click="prevStep"
            v-if="stepIndex !== 0"
            :disabled="isAdvancing"
          >
            Previous
          </button>

          <button
            class="btn btn-primary"
            @click="nextStep"
            :disabled="isAdvancing || stepIndex === steps.length - 1"
          >
            <span v-if="isAdvancing">Loading…</span>
            <span v-else>Next</span>
          </button>

          <button class="btn btn-link" @click="finish" :disabled="isAdvancing">Cancel</button>
        </div>

        <div class="cell small-12">
          <button class="btn btn-primary" @click="finish" v-if="stepIndex === steps.length - 1" :disabled="isAdvancing">
            Finish
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import WizardProgress from '@/components/navigation/WizardProgressBar.vue'

import Step1 from './Add-New-Client-Step1.vue'
import Step2 from './Add-New-Client-Step2.vue'
import Step3 from './Add-New-Client-Step3.vue'
import Step4 from './Add-New-Client-Step4.vue'
import Step5 from './Add-New-Client-Step5.vue'
import Step6 from './Add-New-Client-Step6.vue'
import Step7 from './Add-New-Client-Step7.vue'

import { useIdentityStore } from '@/stores/identity'

const router = useRouter()
const route = useRoute()
const identity = useIdentityStore()

const error = ref(null)
const loaded = ref(false)
const stepIndex = ref(0)
const stepComponentRef = ref(null)

// Option B: prevents double-press / re-entry while nextStep is executing
const isAdvancing = ref(false)

const formData = ref({
  intermediaryId: '',
  // IMPORTANT: this flag must exist for skipping Step 3
  isOnboardingViaIntermediary: false,

  estimatedPayslips: [],
  previousPayrollSoftwareProvider: '',
  industry: '',
  address: {
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    addressLine4: '',
    postcode: '',
    country: ''
  },
  invitee: {
    firstName: '',
    surname: '',
    email: ''
  }
})

const baseSteps = [
  { label: 'Client Signup Type', component: Step1 },
  { label: 'Client Details', component: Step2 },
  { label: 'Contact Details', component: Step3 }, // Step 3 (conditionally removed)
  { label: 'Previous Software Provider', component: Step4 },
  { label: 'Number of Payslips', component: Step5 },
  { label: 'Go-Live Details', component: Step6 },
  { label: 'Complete', component: Step7 }
]

// Steps list that skips Step3 when onboarding via intermediary is true
const steps = computed(() => {
  if (formData.value?.isOnboardingViaIntermediary === true) {
    return baseSteps.filter(s => s.component !== Step3)
  }

  return baseSteps.filter(s => s.component !== Step4)
})

const currentStepComponent = computed(() => steps.value[stepIndex.value]?.component)

onMounted(() => {

  if(identity.type == 'Administrator') {
    formData.value.intermediaryId = route.params.intermediaryId
  }
  else
  {
    formData.value.intermediaryId = identity.intermediary?.intermediaryId
  }
  
  if (identity.type == 'Intermediary' && identity.intermediary.defaultClientOnboardingType == 'IntermediaryOnboarding') {
    formData.value.isOnboardingViaIntermediary = true
  } else {
    formData.value.isOnboardingViaIntermediary = false
  }

  loaded.value = true
})

// If the flag changes and Step3 is removed, keep index in range
watch(
  () => formData.value?.isOnboardingViaIntermediary,
  () => {
    const max = steps.value.length - 1
    if (stepIndex.value > max) stepIndex.value = max
  }
)

const nextStep = async () => {
  // Guard against double-click / re-entry
  if (isAdvancing.value) return
  isAdvancing.value = true

  try {
    // Validate current step
    if (stepComponentRef.value && typeof stepComponentRef.value.validateStep === 'function') {
      const isValid = stepComponentRef.value.validateStep()
      if (!isValid) return
    }

    // Execute current step (eg Step 7 API call)
    if (stepComponentRef.value && typeof stepComponentRef.value.executeStep === 'function') {
      const success = await stepComponentRef.value.executeStep()
      if (!success) return
    }

    error.value = null

    if (stepIndex.value < steps.value.length - 1) {
      stepIndex.value++
    }
  } catch (e) {
    // Optional: surface unexpected errors
    error.value = e?.message ?? 'Something went wrong'
  } finally {
    isAdvancing.value = false
  }
}

const prevStep = () => {
  if (isAdvancing.value) return
  if (stepIndex.value > 0) stepIndex.value--
}

const finish = () => {
  if (isAdvancing.value) return

  if(identity.type == 'Administrator') {
    router.push({ name: 'AdminClientList' })
  }
  else
  {
    router.push({ name: 'IntermediaryList' })
  }
}

const handleFormUpdate = (updatedData) => {
  formData.value = updatedData
}
</script>

<style scoped>
#wizard-breadcrumbs {
  margin-bottom: 40px;
  margin-top: 40px;
}

#wizard-navigation {
  margin-top: 30px;
}
</style>